import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce } from "lodash";
import { UserEntity } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";

interface PublicUsersContextType {
  fetchUser: (id: string) => void;
  users: Record<string, UserEntity>;
  /**
   * The ids we asked `getUsersPublic` about and did not get back.
   *
   * `getPublicUsers` (`core/db/users.ts`) returns only documents that exist,
   * so `users[id]` never arrives for an id with no `public-users-v1`
   * document. That happens for an anonymous share visitor who commented, for
   * a deleted user, and for any id whose public profile nobody ever wrote.
   * Without this set, `useUserCached` holds `userLoading: true` for the rest
   * of the session, and every avatar for that id renders a skeleton forever.
   *
   * Only a resolved fetch adds ids here. A rejected fetch leaves the id
   * unknown on purpose: "the call failed" is not "the profile does not
   * exist", and to mix the two renders the anonymous fallback for a real
   * user whose one request failed.
   */
  absentUserIds: Set<string>;
}

const PublicUsersContext = createContext<PublicUsersContextType>({
  fetchUser: () => {},
  users: {},
  absentUserIds: new Set(),
});

export const PublicUsersContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [users, setUsers] = useState<Record<string, UserEntity>>({});
  const [absentUserIds, setAbsentUserIds] = useState<Set<string>>(new Set());
  const userIdsQueue = useRef<Set<string>>(new Set());

  const fetchUser = useMemo(
    () =>
      debounce(async () => {
        const userIds = Array.from(userIdsQueue.current);
        userIdsQueue.current.clear();
        const userIdsGroups = userIds.reduce(
          (acc, id) => {
            const lastGroup = acc[acc.length - 1];
            if (lastGroup.length < 50) {
              lastGroup.push(id);
            } else {
              acc.push([id]);
            }
            return acc;
          },
          [[]],
        );
        if (userIdsGroups.length > 0) {
          await Promise.all(
            userIdsGroups.map(async (group) => {
              try {
                const users = await highnote.getUsersPublic({
                  ids: group,
                });
                setUsers((prevUsers) => ({ ...prevUsers, ...users }));
                /* Everything we asked for and did not get back has no public
                 * profile document. We record it here so `useUserCached` can
                 * end its `userLoading` state; see `absentUserIds`. */
                const missing = group.filter((id) => !users?.[id]);
                if (missing.length > 0) {
                  setAbsentUserIds((prev) => {
                    const next = new Set(prev);
                    missing.forEach((id) => next.add(id));
                    return next;
                  });
                }
                // eslint-disable-next-line no-empty
              } catch (e) {}
            }),
          );
        }
      }, 500),
    [],
  );

  const values = useMemo(() => {
    return {
      fetchUser: (id: string) => {
        if (!users[id] && !userIdsQueue.current.has(id)) {
          userIdsQueue.current.add(id);
          return fetchUser();
        }
      },
      users,
      absentUserIds,
    };
  }, [fetchUser, users, absentUserIds]);

  return (
    <PublicUsersContext.Provider value={values}>
      {children}
    </PublicUsersContext.Provider>
  );
};

export const usePublicUser = () => useContext(PublicUsersContext);
